/**
 * Oversettelse av grensesnittet.
 *
 * All tekst i koden er skrevet på norsk - t(tekst) viser den norske teksten
 * uendret så lenge språket er 'nb', og slår den opp i en ferdig ordbok for
 * alle andre språk. Ingen egne nøkler å vedlikeholde: den norske teksten ER
 * nøkkelen.
 *
 * Ordbøkene ligger committet i i18n.<kode>.js og bygges av `npm run i18n`,
 * som oversetter det som mangler og lar alt som allerede står der være i fred.
 * Kjøretiden gjør derfor ingenting annet enn oppslag: ingen nettverkskall,
 * ingen venting, ingen tekst som blafrer fra norsk til målspråket etter et
 * halvsekund - siden er riktig fra første bilde, også uten nett.
 *
 * En tekst uten oversettelse vises på norsk. Det er den milde varianten av å
 * være uferdig, og den samme som gjelder for tekster ingen kan fange opp på
 * forhånd (feilmeldinger med tall i seg, f.eks.).
 */
import { computed, reactive } from 'vue'

const SOURCE_LANG = 'nb'
const DEVICE_KEY = 'bonto.i18n.lang'
const LEGACY_PREFIX = 'lommepenger.'

// Alle ordbøkene bakes inn i bygget. Lager `npm run i18n -- --add fr` en ny
// fil, dukker språket opp i velgeren uten at noe annet må endres.
//
// eager, ikke lastet ved behov: hver ordbok er rundt 15 kB, og et par av dem
// koster mindre enn den ekstra rundturen ville gjort. Skulle lista en dag
// vokse til titalls språk, er det her man bytter til dynamisk import - og
// betaler for det med et glimt norsk tekst på første bilde.
const modules = import.meta.glob('./i18n.*.js', { eager: true })

/** { [kode]: { name, strings } } - språkene appen faktisk kan vises på. */
export const dictionaries = Object.fromEntries(
  Object.entries(modules).map(([path, mod]) => [
    path.match(/i18n\.(.+)\.js$/)[1],
    { name: mod.name, strings: mod.strings },
  ]),
)

function known(code) {
  return code === SOURCE_LANG || Object.hasOwn(dictionaries, code)
}

/**
 * Språket nettleseren er satt opp med, redusert til ren språkkode ('en-GB' ->
 * 'en'). Finnes det ingen ordbok for det, faller vi tilbake til norsk.
 */
function browserLanguage() {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const tag of tags) {
    const code = String(tag || '').toLowerCase().split('-')[0]
    if (!code) continue
    // Bokmål, nynorsk og «no» er alle språket appen er skrevet på.
    if (['nb', 'nn', 'no'].includes(code)) return SOURCE_LANG
    if (known(code)) return code
  }
  return SOURCE_LANG
}

function initialDeviceLanguage() {
  try {
    const saved = localStorage.getItem(DEVICE_KEY)
    return saved && known(saved) ? saved : browserLanguage()
  } catch {
    return browserLanguage()
  }
}

// Alt appen la i localStorage het lommepenger.* fram til navnebyttet. Der lå
// også de hentede oversettelsene, én nøkkel per språk pluss et manifest -
// flere hundre kB som ingen leser lenger, siden ordbøkene nå ligger i bygget.
//
// Språkvalget er verdt å ta med seg over: den som har valgt svensk på denne
// enheten skal ikke møte norsk igjen fordi appen skiftet navn. Resten ryddes
// vekk. Hele blokka kan slettes når alle nettlesere har vært innom én gang.
try {
  const legacyLang = localStorage.getItem(`${LEGACY_PREFIX}i18n.lang`)
  if (legacyLang && !localStorage.getItem(DEVICE_KEY)) {
    localStorage.setItem(DEVICE_KEY, legacyLang)
  }
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(LEGACY_PREFIX)) localStorage.removeItem(key)
  }
} catch { /* ikke kritisk */ }

// family er familiens valgte språk og gjelder alle i familien. device er
// valget på denne enheten, og gjelder før noen er logget inn - da finnes det
// ingen familie å hente språk fra, men velkomst- og innloggingssidene skal
// likevel vises på noe brukeren forstår.
const state = reactive({ lang: SOURCE_LANG, family: null, device: initialDeviceLanguage() })

/** Oversetter tekst. vars erstatter {navn}-plassholdere etter oversettelse. */
export function t(text, vars) {
  if (!text) return text

  let out = text
  if (state.lang !== SOURCE_LANG) {
    out = dictionaries[state.lang]?.strings[text] ?? text
  }

  if (vars) {
    for (const [key, value] of Object.entries(vars)) out = out.replaceAll(`{${key}}`, value)
  }
  return out
}

/** Språkvalget på denne enheten. Gjelder til en familie er lastet. */
export const deviceLanguage = computed(() => state.device)

/** Slår sammen de to kildene til ett språk. */
function apply() {
  const wanted = state.family || state.device || SOURCE_LANG
  // Familien kan ha lagret et språk det ikke lenger finnes ordbok for. Da er
  // norsk et ærligere svar enn en halvveis oversatt app.
  state.lang = known(wanted) ? wanted : SOURCE_LANG
}

/** Kalles når familiens språk lastes eller endres. Tomt når ingen er innlogget. */
export function setLanguage(code) {
  state.family = code || null
  apply()
}

/**
 * Valget fra språkknappen på de innloggede sidenes utside. Huskes på enheten,
 * slik at neste besøk starter riktig med én gang. Familiens språk overstyrer
 * det så snart noen logger inn - språket er en familieinnstilling, ikke en
 * personlig en.
 */
export function setDeviceLanguage(code) {
  state.device = code || SOURCE_LANG
  try { localStorage.setItem(DEVICE_KEY, state.device) } catch { /* ikke kritisk */ }
  apply()
}

// Nettleserspråket skal gjelde fra første bilde, ikke først når App er montert.
apply()
